import { type PerDiem } from "@finway-group/shared/lib/models"
import { type FormInstance, type Rule } from "antd/lib/form"
import type moment from "moment"
import { i18n } from "~Shared/locales/i18n"
import { type PerDiemDestinationItinerary } from "../expenseForm/reimbursementForms/perDiem/perDiem.form.types"

type DestinationValidationGeneratorType = {
    name: string
    index: number
    formPart: "date" | "time"
    form: FormInstance<PerDiem>
}

const generatePerDiemEndDateValidator =
    ({ name, index, formPart, form }: DestinationValidationGeneratorType) =>
    (_rule: Rule, value: moment.Moment) => {
        if (!value) {
            return Promise.reject(new Error(i18n.t("validation:required")))
        }
        const tripDestinations: Array<PerDiemDestinationItinerary> = form.getFieldsValue([name])?.destinations ?? []
        const previousDestination = tripDestinations[index - 1]
        if (previousDestination?.endDate && value.isSameOrBefore(previousDestination.endDate, "minute")) {
            return Promise.reject(new Error(i18n.t(`validation:per_diem.end_${formPart}_less_than_last_transfer_${formPart}`)))
        }
        if (tripDestinations[index]?.startDate && value.isSameOrBefore(tripDestinations[index]?.startDate)) {
            if (tripDestinations.length === 1) {
                return Promise.reject(new Error(i18n.t(`validation:per_diem.end_${formPart}_less_than_start_${formPart}`)))
            }
            return Promise.reject(new Error(i18n.t(`validation:per_diem.end_${formPart}_less_than_transfer_${formPart}`)))
        }
        return Promise.resolve()
    }

const generatePerDiemStartDateValidator =
    ({ name, index, formPart, form }: DestinationValidationGeneratorType) =>
    (_rule: Rule, value: moment.Moment) => {
        if (index > 0) {
            return Promise.resolve()
        } // Start date only exist in the 0th index of destinations, so only evaluate for >0 indexes
        if (!value) {
            return Promise.reject(new Error(i18n.t("validation:required")))
        }
        const tripDestinations: Array<PerDiemDestinationItinerary> = form.getFieldsValue([name])?.destinations ?? []
        const currentDestination = tripDestinations[index]

        if (currentDestination?.endDate && value.isSameOrAfter(currentDestination.endDate, "minute")) {
            return Promise.reject(new Error(i18n.t(`validation:per_diem.start_${formPart}_more_than_end_${formPart}`)))
        }

        return Promise.resolve()
    }

export const createDestinationFieldsRule = (destinationFieldName: string, currentIndex: number) => ({
    endDate: [
        (form: FormInstance) => ({ required: true, validator: generatePerDiemEndDateValidator({ name: destinationFieldName, index: currentIndex, formPart: "date", form }) }),
    ] as Array<Rule>,
    endTime: [
        (form: FormInstance) => ({ required: true, validator: generatePerDiemEndDateValidator({ name: destinationFieldName, index: currentIndex, formPart: "time", form }) }),
    ] as Array<Rule>,
    startDate: [
        (form: FormInstance) => ({ required: true, validator: generatePerDiemStartDateValidator({ name: destinationFieldName, index: currentIndex, formPart: "date", form }) }),
    ] as Array<Rule>,
    startTime: [
        (form: FormInstance) => ({ required: true, validator: generatePerDiemStartDateValidator({ name: destinationFieldName, index: currentIndex, formPart: "time", form }) }),
    ] as Array<Rule>,
    destination: [{ required: true, message: i18n.t("validation:required") }] as Array<Rule>,
})

export const createExpenditureRules = (isSynced: boolean) => ({
    syncDestinations: [
        {
            validator: () => {
                if (!isSynced) {
                    return Promise.reject(new Error(i18n.t("validation:per_diem.invalid_table")))
                }
                return Promise.resolve()
            },
        },
    ] as Array<Rule>,
})
